import type { EntityId } from '../sim/entity';
import { STOMP_RANGE } from '../sim/ratSwarm';
import type { Tile } from './autowalk';

/**
 * The rat-pursuit brain (Rat Swarm Slice E, PRD #222 / ADR-0120): the pure
 * decision core behind "click a rat in war mode and race it down". Clicking a
 * rat sets it as the pursuit target; each frame the glue in `main.ts` re-derives
 * the rat's **current** tile from the read-model world (rats wander) and asks
 * {@link pursuitStep} what to do - re-route the autowalk toward a fresh rat
 * tile, keep walking the current route, fire the one `Stomp`, or stand and wait
 * for the authoritative kill.
 *
 * Layered exactly like `autowalk.ts`: this module is pure and node-testable
 * (target selection, the in-range single-fire latch, die-cancel, retarget
 * rules), `main.ts` is the thin glue that owns the sockets, the renderer, and
 * the autowalk controller. State lives in a caller-owned {@link PursuitState}
 * the functions mutate, mirroring `createRepathState`/`nextStep`.
 *
 * Two rules earn a comment:
 *
 * - **Single-fire latch.** A pursuit fires exactly one `Stomp`, the first frame
 *   the player is within {@link STOMP_RANGE}; the latch re-arms only on a fresh
 *   {@link targetRat} (clicking a rat again or another rat), never by drifting
 *   out of range and back in. Kills are not client-predicted, so a fired
 *   pursuit simply waits for the authoritative outcome.
 * - **Die-cancel, not despawn-cancel** (PRD story 34). A killed rat's entity
 *   deliberately lingers ~1s as a corpse so its `die` one-shot can play; the
 *   delta `removed` arrives only after that. Cancelling on removal alone would
 *   ghost-chase the corpse, so the glue routes the `die` action-animation
 *   broadcast to {@link onRatDied} the moment it arrives, and treats the same
 *   dying ids as untargetable for **new** pursuits (the `dying` set handed to
 *   {@link targetRat}). {@link pursuitStep}'s `vanished` branch stays as the
 *   backstop for a rat that leaves the world without a die this client saw
 *   (e.g. the timeout scatter).
 */

/**
 * How close (Chebyshev tiles) the player must be for the stomp to fire - the
 * exact constant the server validates against (`resolveStomp`, ADR-0119),
 * imported rather than re-declared so a server-side tuning can never leave the
 * client firing at the wrong range. Re-exported for the pursuit tests.
 */
export { STOMP_RANGE };

/** The Chebyshev (chessboard) distance between two tiles - UO's range metric. */
export function chebyshev(a: Tile, b: Tile): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** The mutable pursuit state `main.ts` owns; mutated by the functions below. */
export interface PursuitState {
  /** The rat entity being pursued, or `null` when no pursuit is active. */
  target: EntityId | null;
  /** True once this pursuit's single `Stomp` has been fired (see the latch rule). */
  fired: boolean;
  /** The rat tile most recently ordered as the autowalk goal, so an unmoved rat never re-routes. */
  goal: Tile | null;
}

/** A fresh, inactive pursuit state. */
export function createPursuitState(): PursuitState {
  return { target: null, fired: false, goal: null };
}

/**
 * A war-mode click landed on a rat: make it the pursuit target, re-arming the
 * single-fire latch (clicking another rat retargets, PRD story 35). Refused
 * (returns `false`, existing pursuit untouched) when the rat is in `dying` -
 * a lingering corpse whose `die` broadcast this client already saw.
 */
export function targetRat(
  state: PursuitState,
  rat: EntityId,
  dying: ReadonlySet<EntityId>,
): boolean {
  if (dying.has(rat)) return false;
  state.target = rat;
  state.fired = false;
  state.goal = null;
  return true;
}

/** Abandon any active pursuit (manual walk input, war mode toggled off). */
export function cancelPursuit(state: PursuitState): void {
  state.target = null;
  state.fired = false;
  state.goal = null;
}

/**
 * A `die` action-animation arrived for `rat`: if it is the pursued rat, cancel
 * the pursuit immediately (someone else won the race - PRD story 34) and return
 * `true` so the glue also stops the autowalk. Any other rat's death is `false`,
 * pursuit untouched.
 */
export function onRatDied(state: PursuitState, rat: EntityId): boolean {
  if (state.target !== rat) return false;
  cancelPursuit(state);
  return true;
}

/** What the glue should do this frame - one clause per pursuit outcome. */
export type PursuitDecision =
  /** No pursuit is active: nothing to do. */
  | { kind: 'idle' }
  /** The pursued rat left the world (despawn backstop): pursuit cleared, stop walking. */
  | { kind: 'vanished' }
  /** The rat is on a fresh tile: re-route the autowalk to it. */
  | { kind: 'chase'; tile: Tile }
  /** Keep walking the route already ordered (rat unmoved / player tile unknown). */
  | { kind: 'follow' }
  /** In range with the latch armed: fire this pursuit's one Stomp and stop walking. */
  | { kind: 'stomp'; rat: EntityId }
  /** In range, stomp already fired: stand and await the authoritative outcome. */
  | { kind: 'hold' };

/**
 * The per-frame pursuit decision. `playerTile` is the player's current discrete
 * tile (the prediction walker's resting tile), `ratTile` the pursued rat's tile
 * from the read-model world - `undefined` when the entity is gone.
 */
export function pursuitStep(
  state: PursuitState,
  playerTile: Tile | undefined,
  ratTile: Tile | undefined,
): PursuitDecision {
  if (state.target === null) return { kind: 'idle' };
  if (!ratTile) {
    cancelPursuit(state);
    return { kind: 'vanished' };
  }
  if (!playerTile) return { kind: 'follow' };
  if (chebyshev(playerTile, ratTile) <= STOMP_RANGE) {
    if (state.fired) return { kind: 'hold' };
    state.fired = true;
    return { kind: 'stomp', rat: state.target };
  }
  if (state.goal && state.goal.x === ratTile.x && state.goal.y === ratTile.y) {
    return { kind: 'follow' };
  }
  state.goal = { x: ratTile.x, y: ratTile.y };
  return { kind: 'chase', tile: state.goal };
}
